import React, { lazy, Suspense, useMemo, useEffect } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'

import { PrivateRoute } from '../components/private-route'
import { allPlatforms } from '../constants'
import CircularProgress from '../components/circular-progress'
import { useAppSelector } from '../store/hooks'
import { selectAuthCompanySlice } from '../store/auth-company-slice'
import { selectAuthUserSlice } from '../store/auth-user-slice'
import { resolveFeaturePermissions } from '@vacationtracker/shared/functions/resolve-feature-permissions'
import { legacyIntegrationRedirects } from './Integrations/integration-routes'
import IntegrationDetailRoute from './Integrations/detail-route'

// Retry dynamic imports to handle stale chunks after deployments.
// When a new deploy changes chunk hashes, cached HTML may reference old URLs that 404.
// This retries once, then reloads the page to fetch fresh HTML (max once per 5 min).
const RELOAD_KEY = 'lazyWithRetry_lastReload'
function lazyWithRetry(importFn: () => Promise<{ default: React.ComponentType<any> }>) {
  // eslint-disable-line @typescript-eslint/no-explicit-any
  return lazy(() =>
    importFn().catch(() =>
      importFn().catch((error) => {
        const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || '0')
        if (Date.now() - lastReload > 300_000) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
          window.location.reload()
          return new Promise(() => {
            /* never resolves - page is reloading */
          })
        }
        throw error
      })
    )
  )
}

const ExternalApiDocsRedirect = () => {
  useEffect(() => {
    window.location.href = 'https://vacationtracker.io/developers/api'
  }, [])
  return null
}

// Eagerly load the dashboard since it's the most common entry point
import DashboardPage from './DashboardPage'
import DashboardPageForUsers from './DashboardPageForUsers'

// Role-adaptive dashboard (behind feature flag)
const RoleAdaptiveDashboard = lazyWithRetry(() => import('./RoleAdaptiveDashboard'))

// Lazy load all other routes
const RequestLeavePage = lazyWithRetry(() => import('./Leaves/RequestLeavePage'))
const RequestToilPage = lazyWithRetry(() => import('./Toil/RequestToilPage'))
const EditRequestToilPage = lazyWithRetry(() => import('./Toil/EditRequestToilPage'))
const EditLeavePage = lazyWithRetry(() => import('./Leaves/EditLeavePage'))
const AddLeavePage = lazyWithRetry(() => import('./Leaves/AddLeavePage'))
const LeaveRequestsPage = lazyWithRetry(() => import('./Leaves/LeaveRequestsPage'))
const CalendarPage = lazyWithRetry(() => import('./CalendarPage'))
const WallchartPage = lazyWithRetry(() => import('./Wallchart'))
import TeamsFormPage from './Settings/Teams/TeamsFormPage'
import TeamPage from './Settings/Teams/TeamPage'
import TeamsPage from './Settings/Teams/TeamsPage'
import LocationFormPage from './Settings/Locations/LocationFormPage'
import LocationPage from './Settings/Locations/LocationPage'
import LocationsPage from './Settings/Locations/LocationsPage'
import LeavePoliciesPage from './Settings/LeavePoliciesPage'
import LeaveTypesPage from './Settings/LeaveTypesPage'
import HolidaysPage from './HolidaysPage'
import UserPage from './Users/UserPage'
import UsersPage from './Users/UsersPage'
import GeneralPage from './Settings/GeneralPage'
const FeaturesPermissionsPage = lazyWithRetry(() => import('./Settings/FeaturesPermissionsPage'))
import CompanyLogsPage from './Reports/CompanyLogsPage'
import ScheduledReportFormPage from './Reports/ScheduledReports/ScheduledReportFormPage'
import ScheduledReportsPage from './Reports/ScheduledReports/ScheduledReportsPage'
import NotificationsPage from './Notifications/NotificationsPage'
import NotificationsWeeklyDailyFormPage from './Notifications/NotificationsWeeklyDailyFormPage'
import NotificationsBirthdayFormPage from './Notifications/NotificationsBirthdayFormPage'
import BillingPage from './Settings/BillingPage'
import AccountSettingsPage from './Settings/AccountSettings'
import MyProfilePage from './MyProfilePage'
import EnergyLevelPage from './EnergyLevelPage'
import TeamEnergyPage from './TeamEnergyPage'
import NotFoundPage from './NotFoundPage'
import BotUpdatePermissionsPage from './BotUpdatePermissionsPage'
import ManageMicrosoftLicences from './Users/ManageMicrosoftLicences'
import ImportUsersPage from './Users/ImportUsersPage'
import AddonsPage from './AddonsPage'
import AddonFormPage from './Add-ons/AddonFormPage'
import AddonViewPage from './Add-ons/AddonViewPage'
import ImportHolidaysPage from './Settings/Locations/ImportHolidaysPage'
import OutOfOffice from './OutOfOffice'
import ImportsPage from './ImportsPage'
const ImportPage = lazyWithRetry(() => import('./ImportPage'))
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { UserRoleEnum } from '@vacationtracker/shared/types/user'
import CalendarSyncPage from './CalendarSyncPage'
import AIAssistantPage from './AIAssistedOnboarding'
import AIAssistantConversations from './AIAssistantConversations'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import ReportsPage from './Reports'
import LeaveHistoryReportPage from './Report/LeaveHistoryReportPage'
import LeaveBalanceReportPage from './Report/LeaveBalanceReportPage'
import MigrationPage from './MigrationPage'
import LeaveBalanceMonthlyReportPage from './Report/LeaveBalanceMonthlyReportPage'
const ImportEmailUsersPage = lazyWithRetry(() => import('./Users/ImportEmailUsersPage'))
const ApiKeyFormPage = lazyWithRetry(() => import('./Add-ons/PublicApi/ApiKeyFormPage'))
const ApiKeyDetailsPage = lazyWithRetry(() => import('./Add-ons/PublicApi/ApiKeyDetailsPage'))
const WebhooksPage = lazyWithRetry(() => import('./Add-ons/PublicApi/WebhooksPage'))
const WebhookDetailsPage = lazyWithRetry(() => import('./Add-ons/PublicApi/WebhookDetailsPage'))
const WebhookFormPage = lazyWithRetry(() => import('./Add-ons/PublicApi/WebhookFormPage'))
const McpPage = lazyWithRetry(() => import('./Integrations/McpPage'))
const McpClientFormPage = lazyWithRetry(() => import('./Integrations/McpClientFormPage'))
const IntegrationsPage = lazyWithRetry(() => import('./Integrations'))
import AddHolidayPage from './Settings/Locations/AddHolidayPage'
import LeavePoliciesOverviewPage from './Settings/LeavePoliciesOverviewPage'
import BirthdayConfirmationPage from './BirthdayConfirmationPage'

interface IApp {
  match: {
    url: string
  }
}

const allRoles = [UserRoleEnum.Admin, UserRoleEnum.LocationAdmin, UserRoleEnum.Approver, UserRoleEnum.User]
const adminOnly = [UserRoleEnum.Admin]
const adminAndLocationAdmin = [UserRoleEnum.Admin, UserRoleEnum.LocationAdmin]
const adminAndApprover = [UserRoleEnum.Admin, UserRoleEnum.Approver]
const adminLocationAdminAndApprover = [UserRoleEnum.Admin, UserRoleEnum.LocationAdmin, UserRoleEnum.Approver]
const approverAndUser = [UserRoleEnum.Approver, UserRoleEnum.User]
const userOnly = [UserRoleEnum.User]

const App = ({ match }: IApp): React.ReactElement => {
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)

  const resolved = useMemo(() => resolveFeaturePermissions((authCompany as any) || {}), [authCompany?.featurePermissions])
  const wallchartRoles = resolved.featureVisibility.wallchartAccess.includes(UserRoleEnum.Admin)
    ? [...resolved.featureVisibility.wallchartAccess, UserRoleEnum.LocationAdmin]
    : resolved.featureVisibility.wallchartAccess
  const reportsRoles = resolved.featureVisibility.reportsAccess.includes(UserRoleEnum.Admin)
    ? [...resolved.featureVisibility.reportsAccess, UserRoleEnum.LocationAdmin]
    : resolved.featureVisibility.reportsAccess
  const energyLevelRoles = resolved.featureVisibility.energyLevelAccess
  // Unlike wallchart/reports, location admins are not granted team energy by default;
  // only when they are also an approver (their role is masked to LocationAdmin, so check approverTo).
  const teamEnergyRoles =
    authUser.approverTo.length > 0 && resolved.featureVisibility.teamEnergyAccess.includes(UserRoleEnum.Admin)
      ? [...resolved.featureVisibility.teamEnergyAccess, UserRoleEnum.LocationAdmin]
      : resolved.featureVisibility.teamEnergyAccess
  // A location admin who is also an approver manages addons like an approver (role is masked to LocationAdmin).
  const addonManageRoles = authUser.approverTo.length > 0 ? [...adminAndApprover, UserRoleEnum.LocationAdmin] : adminAndApprover

  return (
    <div className="main-content-wrapper">
      <Suspense fallback={<CircularProgress />}>
        <Switch>
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/leaves/request-leave`} component={RequestLeavePage} />
          <PrivateRoute roles={userOnly} platform={allPlatforms} path={`${match.url}app/request-leave`} component={RequestLeavePage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/leaves/:id/edit`} component={EditLeavePage} />
          <PrivateRoute roles={adminLocationAdminAndApprover} platform={allPlatforms} path={`${match.url}app/leaves/add-leave`} component={AddLeavePage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/leaves/leave-requests/:leaveRequestId`} component={LeaveRequestsPage} />
          <PrivateRoute
            roles={allRoles}
            platform={allPlatforms}
            path={`${match.url}app/leaves/toil/:id/edit`}
            plan={SubscriptionPlanEnum.complete}
            featureFlag={FeatureFlagEnum.toil}
            component={EditRequestToilPage}
          />
          <PrivateRoute
            roles={allRoles}
            platform={allPlatforms}
            path={`${match.url}app/leaves/request-toil`}
            plan={SubscriptionPlanEnum.complete}
            featureFlag={FeatureFlagEnum.toil}
            component={RequestToilPage}
          />
          <PrivateRoute
            roles={allRoles}
            platform={allPlatforms}
            path={`${match.url}app/request-toil`}
            plan={SubscriptionPlanEnum.complete}
            featureFlag={FeatureFlagEnum.toil}
            component={RequestToilPage}
          />
          <PrivateRoute roles={adminLocationAdminAndApprover} platform={allPlatforms} path={`${match.url}app/home`} component={RoleAdaptiveDashboard} />
          <PrivateRoute roles={adminLocationAdminAndApprover} platform={allPlatforms} path={`${match.url}app/dashboard`} component={DashboardPage} />
          <PrivateRoute roles={userOnly} platform={allPlatforms} path={`${match.url}app/my-dashboard`} component={DashboardPageForUsers} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/birthday-confirmation`} component={BirthdayConfirmationPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar/sync/ical/create`} component={CalendarSyncPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar/sync/ical/summary`} component={CalendarSyncPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar/sync/google/connect`} component={CalendarSyncPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar/sync/google/reconnect`} component={CalendarSyncPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar/sync/google/create`} component={CalendarSyncPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar/sync/google/update`} component={CalendarSyncPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar/sync/outlook/connect`} component={CalendarSyncPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar/sync/outlook/reconnect`} component={CalendarSyncPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar/sync/outlook/create`} component={CalendarSyncPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar/sync/outlook/update`} component={CalendarSyncPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar/sync`} component={CalendarSyncPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/calendar`} component={CalendarPage} />
          <PrivateRoute roles={wallchartRoles} platform={allPlatforms} path={`${match.url}app/wallchart`} component={WallchartPage} />
          <PrivateRoute roles={adminAndLocationAdmin} platform={allPlatforms} path={`${match.url}app/settings/departments/create`} component={TeamsFormPage} />
          <PrivateRoute roles={adminAndLocationAdmin} platform={allPlatforms} path={`${match.url}app/settings/departments/:id/edit`} component={TeamsFormPage} />
          <PrivateRoute roles={adminAndLocationAdmin} platform={allPlatforms} path={`${match.url}app/settings/departments/:id`} component={TeamPage} />
          <PrivateRoute roles={adminAndLocationAdmin} platform={allPlatforms} path={`${match.url}app/settings/departments`} component={TeamsPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/settings/locations/create`} component={LocationFormPage} />
          {/* Editing moved onto the location page itself; old links and bookmarks land on General. */}
          <Route
            exact
            path={`${match.url}app/settings/locations/:id/edit`}
            render={({ match: editMatch }) => <Redirect to={`/app/settings/locations/${editMatch.params.id}/general`} />}
          />
          <PrivateRoute roles={adminAndLocationAdmin} platform={allPlatforms} path={`${match.url}app/settings/locations/:id/:type`} component={LocationPage} />
          <PrivateRoute
            roles={adminAndLocationAdmin}
            platform={allPlatforms}
            path={`${match.url}app/settings/location/:locationId/leave-policies/:leaveTypeId`}
            component={LeavePoliciesPage}
          />
          <PrivateRoute roles={adminAndLocationAdmin} platform={allPlatforms} path={`${match.url}app/settings/leave-policies`} component={LeavePoliciesOverviewPage} />
          <PrivateRoute roles={adminAndLocationAdmin} platform={allPlatforms} path={`${match.url}app/settings/locations`} component={LocationsPage} />
          <PrivateRoute roles={adminAndLocationAdmin} platform={allPlatforms} path={`${match.url}app/settings/leave-types`} component={LeaveTypesPage} />
          <PrivateRoute roles={approverAndUser} platform={allPlatforms} path={`${match.url}app/holidays`} component={HolidaysPage} />
          <PrivateRoute roles={adminAndLocationAdmin} platform={allPlatforms} path={`${match.url}app/import-holidays/:id/:year?`} component={ImportHolidaysPage} />
          <PrivateRoute roles={adminAndLocationAdmin} platform={allPlatforms} path={`${match.url}app/holiday/:locationId/:holidayId?`} component={AddHolidayPage} />
          <PrivateRoute roles={adminOnly} platform={['microsoft']} path={`${match.url}app/users/manage-licences`} component={ManageMicrosoftLicences} />
          <PrivateRoute roles={adminOnly} platform={['microsoft', 'slack', 'google']} path={`${match.url}app/users/import`} component={ImportUsersPage} />
          <PrivateRoute roles={adminOnly} platform={['email']} path={`${match.url}app/users/import-email-users`} component={ImportEmailUsersPage} />
          <PrivateRoute roles={adminLocationAdminAndApprover} platform={allPlatforms} path={`${match.url}app/users/:id`} component={UserPage} />
          <PrivateRoute roles={adminLocationAdminAndApprover} platform={allPlatforms} path={`${match.url}app/users`} component={UsersPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/settings/features-permissions`} component={FeaturesPermissionsPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/settings/general`} component={GeneralPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/settings/personal`} component={AccountSettingsPage} />
          <PrivateRoute
            roles={adminLocationAdminAndApprover}
            platform={allPlatforms}
            path={`${match.url}app/notifications/:id/edit`}
            component={NotificationsWeeklyDailyFormPage}
          />
          <PrivateRoute roles={adminLocationAdminAndApprover} platform={allPlatforms} path={`${match.url}app/notifications/create`} component={NotificationsWeeklyDailyFormPage} />
          <PrivateRoute
            roles={adminLocationAdminAndApprover}
            platform={allPlatforms}
            path={`${match.url}app/notifications/birthday/:id/edit`}
            component={NotificationsBirthdayFormPage}
          />
          <PrivateRoute
            roles={adminLocationAdminAndApprover}
            platform={allPlatforms}
            path={`${match.url}app/notifications/birthday/create`}
            component={NotificationsBirthdayFormPage}
          />
          <PrivateRoute roles={adminLocationAdminAndApprover} platform={allPlatforms} path={`${match.url}app/notifications`} component={NotificationsPage} />
          <PrivateRoute roles={teamEnergyRoles} platform={allPlatforms} path={`${match.url}app/team-energy`} component={TeamEnergyPage} />
          {legacyIntegrationRedirects(match.url)}
          <PrivateRoute
            roles={adminLocationAdminAndApprover}
            platform={allPlatforms}
            path={`${match.url}app/add-ons/:addonType/view/documentation`}
            component={ExternalApiDocsRedirect}
          />
          <PrivateRoute roles={adminLocationAdminAndApprover} platform={allPlatforms} path={`${match.url}app/add-ons/:addonType/view`} component={AddonViewPage} />
          <PrivateRoute roles={addonManageRoles} platform={allPlatforms} path={`${match.url}app/add-ons/:addonType/create`} component={AddonFormPage} />
          <PrivateRoute roles={addonManageRoles} platform={allPlatforms} path={`${match.url}app/add-ons/:addonType/:id/edit`} component={AddonFormPage} />
          <PrivateRoute roles={adminLocationAdminAndApprover} platform={allPlatforms} path={`${match.url}app/add-ons`} component={AddonsPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/integrations/api/keys/create`} component={ApiKeyFormPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/integrations/api/keys/:keyId/edit`} component={ApiKeyFormPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/integrations/api/keys/:keyId`} component={ApiKeyDetailsPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/integrations/webhooks/create`} component={WebhookFormPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/integrations/webhooks/:webhookId`} component={WebhookDetailsPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/integrations/webhooks`} component={WebhooksPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/integrations/mcp/clients/create`} component={McpClientFormPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/integrations/mcp`} component={McpPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/integrations/:integrationType`} component={IntegrationDetailRoute} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/integrations`} component={IntegrationsPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/imports`} component={ImportsPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/import`} component={ImportPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/settings/billing`} component={BillingPage} />
          <PrivateRoute roles={reportsRoles} platform={allPlatforms} path={`${match.url}app/reports/scheduled-reports/:id/edit`} component={ScheduledReportFormPage} />
          <PrivateRoute roles={reportsRoles} platform={allPlatforms} path={`${match.url}app/reports/company-logs`} component={CompanyLogsPage} />
          <PrivateRoute roles={reportsRoles} platform={allPlatforms} path={`${match.url}app/reports/scheduled-reports/create`} component={ScheduledReportFormPage} />
          <PrivateRoute roles={reportsRoles} platform={allPlatforms} path={`${match.url}app/reports/scheduled-reports`} component={ScheduledReportsPage} />
          <PrivateRoute roles={reportsRoles} platform={allPlatforms} path={`${match.url}app/reports/leave-history-report`} component={LeaveHistoryReportPage} />
          <PrivateRoute roles={reportsRoles} platform={allPlatforms} path={`${match.url}app/reports/leave-balance-report`} component={LeaveBalanceReportPage} />
          <PrivateRoute roles={reportsRoles} platform={allPlatforms} path={`${match.url}app/reports/leave-balance-monthly-report`} component={LeaveBalanceMonthlyReportPage} />
          <PrivateRoute roles={reportsRoles} platform={allPlatforms} path={`${match.url}app/reports`} component={ReportsPage} />
          <PrivateRoute roles={approverAndUser} platform={allPlatforms} path={`${match.url}app/my-profile`} component={MyProfilePage} />
          <PrivateRoute roles={energyLevelRoles} platform={allPlatforms} path={`${match.url}app/energy-level`} component={EnergyLevelPage} />
          <PrivateRoute roles={allRoles} platform={['slack']} path={`${match.url}app/bot-update-permissions`} component={BotUpdatePermissionsPage} />
          <PrivateRoute roles={allRoles} platform={allPlatforms} path={`${match.url}app/out-of-office`} component={OutOfOffice} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/ai-assistant/:id`} component={AIAssistantPage} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/ai-assistant`} component={AIAssistantConversations} />
          <PrivateRoute roles={adminOnly} platform={allPlatforms} path={`${match.url}app/migration`} component={MigrationPage} />
          <Route exact path={`${match.url}app`} render={({ location }) => <Redirect to={{ pathname: '/', search: location.search }} />} />
          <Route exact path={`${match.url}`} render={() => <CircularProgress />} />
          <Route component={NotFoundPage} />
        </Switch>
      </Suspense>
    </div>
  )
}

export default App
